import React, { useEffect, useState } from "react";
import ReactTooltip from "react-tooltip";

import Button from "@cayuse/shared-react.components.button";
import { Combobox } from "@cayuse/shared-react.components.form.combobox";
import Radio from "@cayuse/shared-react.components.form.inputs.radio";
import TextInput from "@cayuse/shared-react.components.form.inputs.text";
import IconButton from "@cayuse/shared-react.components.icon-button";
import Modal from "@cayuse/shared-react.components.modal";
import SearchDropdown from "@cayuse/shared-react.components.search-dropdown";
import { isNonEmptyObject, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { Modal as SemanticModal, Transition } from "semantic-ui-react";
import { api } from "services";

import SearchBar from "components/SearchBar";
import { ERROR_STATUS_CODE_400, ERROR_STATUS_CODE_403, ERROR_STATUS_CODE_404, ERROR_STATUS_CODE_500, TRANSITION_DURATION } from "globals/constants";
import { useDictionary } from "hooks";
import { debounce, notifyError, notifySuccess, validateTitleLength, searchUnits } from "utils";
import { responseErrorHandler } from "utils/errorHandler";

import styles from "./styles.css";

const MODAL_ID_BASE = "newAgreementModal";

const DEFAULT_DICTIONARY = {
	loadStatusesError: "Unable to retrieve agreement information, please refresh page or come back to this tab later.",
	searchError: "Unable to retrieve matching agreement information, please try again later.",
	createError: "Unable to create agreement, please try again later.",
	linkError: "Unable to link agreement, please try again later.",
};

const actions = {
	create: "create",
	link: "link"
};

const ICONS = {
	StartIcon: (
		<svg
			aria-hidden="true"
			className="svg-inline--fa fa-magnifying-glass icon solid"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 512 512"
		>
			<path
				fill="currentColor"
				d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"
			/>
		</svg>
	),
	DropdownIcon: (
		<svg
			height="20"
			width="20"
			viewBox="0 0 20 20"
			aria-hidden="true"
			focusable="false"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				fill="currentColor"
				d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
			/>
		</svg>
	),
	ClearIcon: (
		<svg
			height="20"
			width="20"
			viewBox="0 0 20 20"
			aria-hidden="true"
			focusable="false"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
		</svg>
	),
};

const StartNewAgreement = ({ awardId, projectId, hasCreatePermission, refresh }) => {
	const dictionary = useDictionary("agreements.startNewAgreement");
	const [ isLoading, setIsLoading ] = useState(false);
	const [ action, setAction ] = useState();
	const [ types, setTypes ] = useState([]);
	const [ statuses, setStatuses ] = useState([]);
	const [ visible, setVisible ] = useState();
	const [ open, setOpen ] = useState();
	const [ searchField, setSearchField ] = useState();
	const [ confirmDisabled, setConfirmDisabled ] = useState();
	const [ disabledReason, setDisabledReason ] = useState(null);
	const [ agreementsOptions, setAgreementsOptions ] = useState([]);
	const [ searchInput, setSearchInput ] = useState();
	const [ agreement, setAgreement ] = useState();

	const ariaControlsAttr = open ? { [`aria-controls`]: MODAL_ID_BASE } : {};

	useEffect(() => {
		const load = async () => {
			try {
				const [ typeList, statusList ] = await Promise.all([ api.agreements.getTypes(), api.agreements.getStatuses() ]);
				const sortedTypes = typeList
					.map(type => ({ key: type.id, label: type.name, value: type.name }))
					.sort((a,b) => a.label.localeCompare(b.label));
				setTypes(sortedTypes);
				const sortedStatuses = statusList
					.map(status => ({ key: status.id, label: status.name, value: status.name }))
					.sort((a,b) => a.label.localeCompare(b.label));
				setStatuses(sortedStatuses);
			} catch (error) {
				responseErrorHandler(error, {
					[ERROR_STATUS_CODE_400]: dictionary.errors.loadStatusesError || DEFAULT_DICTIONARY.loadStatusesError,
					[ERROR_STATUS_CODE_403]: dictionary.errors.loadStatusesError || DEFAULT_DICTIONARY.loadStatusesError,
					[ERROR_STATUS_CODE_404]: dictionary.errors.loadStatusesError || DEFAULT_DICTIONARY.loadStatusesError,
					[ERROR_STATUS_CODE_500]: dictionary.errors.loadStatusesError || DEFAULT_DICTIONARY.loadStatusesError,
				});
				setTypes([]);
				setStatuses([]);
			}
		};

		if(open) {
			load();
		}
	}, [ open ]);

	useEffect(() => {
		const errors = dictionary.errors || {};
		let reason = null;

		if (confirmDisabled) {
			if (!action) {
				reason = errors.noActionSelected || "Please select an option";
			} else if (action === actions.create) {
				reason = errors.noData || "Please enter valid data";
			} else {
				reason = errors.noAgreementSelected || "Please select an agreement";
			}
		}

		if (reason !== disabledReason) {
			setDisabledReason(reason);
		}
	}, [ action, confirmDisabled, dictionary.errors, disabledReason, searchField ]);

	useEffect(() => {
		setAgreementsOptions([]);
		if(isNonEmptyString(searchInput) && action === actions.link) {
			api.agreements.findAll(searchInput)
				.then(results => {
					const updatedOptions = results?.content?.map((item, index) => {
						const textString = `${item.agreementId} : ${item.agreementTitle} - ${item.primaryInvestigators}`;

						if(isNonEmptyString(item.agreementId) && isNonEmptyString(item.agreementTitle)) {
							return {
								content: textString,
								key: `${item.agreementId}`,
								value: item.agreementId,
								original: textString,
								text: `Result ${index + 1} of ${results?.content?.length} for ${searchInput}: ${textString}`,
							};
						}

						return { key: "undefined" };
					});

					const toManyResultsText = addToManyResultsText(updatedOptions, results?.totalElements, searchInput);

					if(toManyResultsText) {
						updatedOptions.push(toManyResultsText);
					}

					// Remove duplicate projects
					const ids = updatedOptions.map(option => option.key);
					const filteredOptions = updatedOptions.filter(({ key }, index) => (
						!ids.includes(key, index + 1) && key !== "undefined"
					));

					setAgreementsOptions(filteredOptions);
				})
				.catch( error =>{
					setAgreementsOptions([]);
					setIsLoading(false);
					responseErrorHandler(error, {
						[ERROR_STATUS_CODE_400]: dictionary.errors.searchError || DEFAULT_DICTIONARY.searchError,
						[ERROR_STATUS_CODE_403]: dictionary.errors.searchError || DEFAULT_DICTIONARY.searchError,
						[ERROR_STATUS_CODE_404]: dictionary.errors.searchError || DEFAULT_DICTIONARY.searchError,
						[ERROR_STATUS_CODE_500]: dictionary.errors.searchError || DEFAULT_DICTIONARY.searchError,
					});
				})
				.finally(() => setIsLoading(false));
		}
	}, [ action, searchInput ]);

	useEffect(() => {
		if (visible) {
			ReactTooltip.rebuild();
		}
	}, [ visible ]);

	useEffect(() => {
		if (action === actions.create) {
			const valid = !!agreement &&
				!!agreement.title &&
				validateTitleLength(agreement.title) &&
				!!agreement.typeId &&
				!!agreement.statusId &&
				!!agreement.unitId;
			setConfirmDisabled(!valid);
		} else if (action === actions.link) {
			setConfirmDisabled(!searchField);
		}
	}, [ action, agreement, searchField ]);

	const onActionChange = value => {
		setAction(value);

		if (!visible) {
			setVisible(true);
		}
	};

	const onOpen = () => {
		if(hasCreatePermission) {
			setAction(null);
			setVisible(false);
		} else {
			setVisible(true);
			setAction(actions.link);
		}
		setAgreement(null);
		setSearchField(null);
		setSearchInput("");
		setConfirmDisabled(true);
		setOpen(true);
	};

	const onClose = () => {
		setOpen(false);
	};

	const onSave = async () => {
		let agreementId = searchField;
		let message = `Agreement ${agreementId} has been linked successfully`;
		setIsLoading(true);
		if (action === actions.create) {
			try {
				agreementId = await api.agreements.create({
					title: agreement.title,
					unitId: agreement.unitId,
					statusId: agreement.statusId,
					typeId: agreement.typeId,
				}, { notifyUponError: false });
				message = `Agreement ${agreementId} has been created and linked successfully`;
			} catch (error) {
				setIsLoading(false);
				responseErrorHandler(error, {
					[ERROR_STATUS_CODE_400]: dictionary.errors.createError || DEFAULT_DICTIONARY.createError,
					[ERROR_STATUS_CODE_403]: dictionary.errors.createError || DEFAULT_DICTIONARY.createError,
					[ERROR_STATUS_CODE_500]: dictionary.errors.createError || DEFAULT_DICTIONARY.createError,
				});
			}
		}

		if (!agreementId) {
			notifyError("Agreement ID is not found");

			return null;
		}

		try {
			await api.agreements.link({ awardId, projectId, agreementId }, { notifyUponError: false });
			refresh();
			onClose();
			notifySuccess(message);
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			responseErrorHandler(error, {
				[ERROR_STATUS_CODE_400]: dictionary.errors.linkError || DEFAULT_DICTIONARY.linkError,
				[ERROR_STATUS_CODE_403]: dictionary.errors.linkError || DEFAULT_DICTIONARY.linkError,
				[ERROR_STATUS_CODE_500]: dictionary.errors.linkError || DEFAULT_DICTIONARY.linkError,
			});
		}
	};

	const onSearchChange = debounce(searchQuery => {
		setSearchInput(searchQuery);
	}, 750);

	const addToManyResultsText = (options, totalElements, searchInput) => {
		if(options?.length < totalElements) {
			return {
				key: "refine-results",
				value: "refine-results",
				text: `There are too many results for "${searchInput}", try adding to your search to see more specific results`,
				disabled: true
			};
		}

		return false;
	};

	const handleOnChange = (agreementId, options) => {
		// Used to change the display text of the selected item
		const updateDisplayText = () => {
			// find the selected option and restore the original value
			// const selected = _find(options, { value: value });
			const selected = options.find(option => option.key === agreementId);

			if(isNonEmptyObject(selected)) {
				selected.text = selected.original;
			}

			const updatedOptions = options.map(option => option.key === selected.key ? selected : option);

			// now set only one option in the list of results
			setAgreementsOptions(updatedOptions);
		};

		if(isNonEmptyString(agreementId)) {
			updateDisplayText();
			setSearchField(agreementId);
			setConfirmDisabled(false);
		} else {
			setSearchInput("");
			setSearchField(null);
			setConfirmDisabled(true);
		}
	};

	const titleChanged = event => {
		const value = event.target.value;
		setAgreement(prevState => ({
			...prevState,
			title: value
		}));
	};

	return (
		<Modal
			id={MODAL_ID_BASE}
			ariaLabel={dictionary.createNew || "Add Related Record"}
			className={styles.modal}
			closeIcon
			trigger={(
				<IconButton
					icon="plus"
					labelPosition="left"
					primary
					{...ariaControlsAttr}
					onClick={onOpen}
				>
					{dictionary.createNew || "Add Related Record"}
				</IconButton>
			)}
			open={open}
			onClose={onClose}
			size="one-half"
		>
			<SemanticModal.Header className={`ui header ${styles.modalHeading} whiteOnBlue`}>
				<h2>{dictionary.createNew || "Add Related Record"}</h2>
			</SemanticModal.Header>
			<SemanticModal.Content>
				<div className={`ui form ${styles.agreementForm}`}>
					<Radio
						id={actions.link}
						name="actionGroup"
						checked={action === actions.link}
						value={actions.link}
						onChange={() => onActionChange(actions.link)}
					>
						<span
							dangerouslySetInnerHTML={{
								__html: dictionary.linkToExistingAgreement || "Link an existing Agreement record"
							}}
						/>
					</Radio>

					{visible && action === actions.link &&
						<div className={styles.dropdownWrapper}>
							<SearchBar
								id="existingAgreementSearch"
								className={styles.modalInput}
								options={agreementsOptions}
								onChange={(event, { value }) => handleOnChange(value, agreementsOptions)}
								onSearchChange={(event, { searchQuery }) => onSearchChange(searchQuery)}
								value={searchField}
								placeholder={dictionary.searchPlaceholder || "Search for Agreement by ID, Title, or Principal Investigator"}
							/>
						</div>
					}

					{hasCreatePermission && (
						<Radio
							id={actions.create}
							name="actionGroup"
							checked={action === actions.create}
							value={actions.create}
							onChange={() => onActionChange(actions.create)}
						>
							<span
								dangerouslySetInnerHTML={{
									__html: dictionary.createAgreemnet || "Create and link a new Agreement record"
								}}
							/>
						</Radio>
					)}

					{hasCreatePermission && visible && action === actions.create &&
						<div className={styles.formWrapper}>
							<div className={styles.formField}>
								<label className={styles.label}>
									<span className={styles.requiredIndicator} aria-hidden="true">*</span>
									{dictionary.agreementTitle || "Agreement Title"}
								</label>

								<TextInput
									autoFocus
									className={styles.inputBox}
									onChange={titleChanged}
									aria1-label="Agreement Title"
									value={agreement?.title}
									required={true}
									placeholder={dictionary.enterTitle || "Enter Agreement Title"}
								/>
								{!validateTitleLength(agreement?.title ?? "") && <span className={styles.titleLengthWarning}>
									{dictionary.errors.titleLengthValidation ?? "Agreement title must be less than 256 characters."}
								</span>}
							</div>
							<div className={styles.formField}>
								<label className={styles.label}>
									<span className={styles.requiredIndicator} aria-hidden="true">*</span>
									{dictionary.agreementType || "Agreement Type"}
								</label>

								<Combobox
									onChange={option =>setAgreement(prevState => ({
										...prevState,
										type: option,
										typeId: option?.key ?? null
									}))}
									options={types}
									placeholder={dictionary.selectType || "Select Agreement Type"}
									value={agreement?.type}
									components={ICONS}
								/>
							</div>
							<div className={styles.formField} data-testid="unit-finder">
								<label className={styles.label}>
									<span className={styles.requiredIndicator} aria-hidden="true">*</span>
									{dictionary.adminUnit || "Admin Unit"}
								</label>
								<SearchDropdown
									value={agreement?.unitId}
									search={options => options}
									selection
									selectOnNavigation={false}
									forceSelection={false}
									selectOnBlur={false}
									onGetDataPromise={searchUnits}
									searchingMessage={"Searching"} // todo: dictionary
									placeholder={dictionary.selectUnit || "Search for a Unit"}
									onChange={ (event , data) => setAgreement(prevState => ({
										...prevState,
										unitId: data[0]?.key ?? null
									}))}
									narrowCriteriaHint={{ show: true }}
									className="unitFinder"
									icon={
										<span
											className="caret-icon"
											aria-hidden="true"
										>
											<svg
												height="20"
												width="20"
												viewBox="0 0 20 20"
												aria-hidden="true"
												focusable="false"
											>
												<path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
											</svg>
										</span>
									}
									clearIcon={
										<button type="button" aria-label="Clear" className={styles.clearButton}>
											<span className="clear-icon">
												<svg
													height="20"
													width="20"
													viewBox="0 0 20 20"
												>
													<path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
												</svg>
											</span>
										</button>
									}
								/>
							</div>
							<div className={styles.formField}>
								<label className={styles.label}>
									<span className={styles.requiredIndicator} aria-hidden="true">*</span>
									{dictionary.agreementStatus || "Agreement Status"}
								</label>

								<Combobox
									onChange={option =>setAgreement(prevState => ({
										...prevState,
										status: option,
										statusId: option?.key ?? null
									}))}
									options={statuses}
									placeholder={dictionary.selectStatus || "Select Agreement Status"}
									value={agreement?.status}
									components={ICONS}
								/>
							</div>
						</div>
					}
				</div>
			</SemanticModal.Content>
			<SemanticModal.Actions>
				<Transition animation="scale right" duration={TRANSITION_DURATION}>
					<div className="field">
						<Button
							secondary
							onClick={onClose}
							data-testid="cancel-button"
						>
							{dictionary.cancel || "Cancel"}
						</Button>
						<Button
							className={styles.confirmButton}
							onClick={onSave}
							disabled={confirmDisabled || isLoading}
							busy={isLoading}
							loadingMessage={dictionary.confirm || "Add Link"}
							tooltip={confirmDisabled && disabledReason}
							data-testid="add-link-button"
						>
							{dictionary.confirm || "Add Link"}
						</Button>
					</div>
				</Transition>
			</SemanticModal.Actions>
		</Modal>
	);
};

export default StartNewAgreement;
